/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Warning } from '../../../store/warningsForm/types';
import { PublicWarningStatus } from '../../../store/warningsForm/types';
import { mapProductStatusToWarningStatus } from '../../../utils/sig-airmet-util';

export type BatchSelectionState = 'none' | 'some' | 'all';

export const getWarningStatus = (warning: Warning): PublicWarningStatus =>
  warning.status ?? mapProductStatusToWarningStatus(warning.productStatus);

export const isWarningSelectableForBatchDelete = (
  warning: Warning,
): boolean => {
  const status = getWarningStatus(warning);
  return (
    status === PublicWarningStatus.DRAFT ||
    status === PublicWarningStatus.DRAFT_PUBLISHED ||
    status === PublicWarningStatus.TODO
  );
};

export const getBatchDeletableWarningIds = (warnings: Warning[]): string[] =>
  warnings
    .filter(isWarningSelectableForBatchDelete)
    .map((warning) => warning.id)
    .filter((id): id is string => !!id);

// Event rows share one event: deleting one deletes all, so they toggle together
export const getBatchDeleteToggleIds = (
  warningId: string,
  warnings: Warning[],
): string[] => {
  const warning = warnings.find((item) => item.id === warningId);
  if (warning?.type !== 'warningsEvent' || !warning.eventId) {
    return [warningId];
  }
  return getBatchDeletableWarningIds(
    warnings.filter(
      (item) =>
        item.type === 'warningsEvent' && item.eventId === warning.eventId,
    ),
  );
};

export const getBatchSelectionState = (
  selectedIds: string[],
  deletableIds: string[],
): BatchSelectionState => {
  if (deletableIds.length === 0) {
    return 'none';
  }

  const selectedIdSet = new Set(selectedIds);
  const selectedDeletableCount = deletableIds.filter((id) =>
    selectedIdSet.has(id),
  ).length;

  if (selectedDeletableCount === 0) {
    return 'none';
  }

  if (selectedDeletableCount === deletableIds.length) {
    return 'all';
  }

  return 'some';
};
